/**
 * What an audio export needs from the host, as types the core can hold.
 *
 * The mirror of `VideoExport.ts`, and deliberately the same shape: an opaque
 * chunk the core carries without looking inside, an adapter that consumes it,
 * and a capability probe. `AudioEncoder`, `AudioData` and `AudioBuffer` are all
 * browser globals and none of them appear here, which is what keeps
 * `src/visual/` compiling with `lib: ["ES2022"], types: []`.
 *
 * ## Which audio this is
 *
 * The **original decoded PCM of the song**, and nothing else. There is a second
 * thing in this codebase that could be mistaken for an audio source —
 * `AudioFeatureTimeline`, the band energies and onsets the visuals react to —
 * and it is not one. Those features are an analysis: mono, heavily downsampled,
 * windowed, and lossy in every way that matters to a listener. They exist to
 * decide what the picture does. Encoding them would produce a file whose audio
 * track is a rendering of the analysis rather than the music.
 *
 * ## Time
 *
 * `t = 0` is the first sample of the song file. That is worth stating because
 * the chart has its own origin — `firstBeatOffsetSeconds` — and the two are not
 * the same instant. The audio track carries the song from its own zero; the
 * visuals follow the replay timeline, which is already expressed in song time.
 * Confusing the two shifts the whole soundtrack by the offset, which sounds
 * exactly like a sync bug and is not one.
 */

// ---------------------------------------------------------------------------
// The PCM the export reads from
// ---------------------------------------------------------------------------

/**
 * The song's samples, as the core is allowed to see them.
 *
 * A *reader*, not a buffer. The decoded song is already in memory once — it is
 * what the player is playing — and an export that began by copying all of it
 * would double the largest allocation in the application for no reason. So the
 * core asks for one chunk at a time and the host copies just that much.
 *
 * Channels are kept separate. Every encoder this targets wants planar float,
 * and interleaving here only to de-interleave there would be two passes over
 * every sample of the song.
 */
export interface PcmSource {
  readonly sampleRate: number;
  readonly channelCount: number;
  /** Total sample frames. One frame is one sample across every channel. */
  readonly frameCount: number;
  /** Length in seconds, from the sample count rather than from any metadata. */
  readonly duration: number;
  /**
   * Copies `frameCount` frames from `startFrame` into `destination`.
   *
   * `destination` is planar: one array per channel, each at least `frameCount`
   * long. Reads past the end are zero-filled rather than refused, so the final
   * partial chunk needs no special case at the call site.
   */
  read(startFrame: number, frameCount: number, destination: readonly Float32Array[]): void;
}

/**
 * One chunk of PCM on its way to the encoder.
 *
 * Opaque for the same reason `CapturedFrame` is: on a real host this owns an
 * `AudioData`, which holds a decoder resource that must be released exactly
 * once. The core knows the chunk exists, when it starts, how long it is, and
 * when it stops being needed.
 */
export interface AudioChunk {
  readonly index: number;
  readonly startFrame: number;
  readonly frameCount: number;
  /** Microseconds from the first sample of the song. See `audioTimestampMicros`. */
  readonly timestampMicros: number;
  readonly durationMicros: number;
  close(): void;
}

/** Turns PCM into chunks the encoder can take. The host supplies this. */
export interface AudioChunkSource {
  readonly sampleRate: number;
  readonly channelCount: number;
  /**
   * Builds one chunk. Returns null when the host could not allocate it, which
   * the session treats as a dropped chunk rather than a failure — the same
   * contract `FrameSource.capture` follows.
   */
  take(index: number, startFrame: number, frameCount: number): AudioChunk | null;
  /** How many chunks are still open. Watched for leaks. */
  readonly liveChunks: number;
  dispose(): void;
}

/** One encoded audio chunk, as much of it as the core needs to pass it on. */
export interface EncodedAudioChunkLike {
  readonly type: 'key' | 'delta';
  readonly timestampMicros: number;
  readonly durationMicros: number | null;
  readonly byteLength: number;
  copyTo(destination: Uint8Array): void;
  /**
   * The encoder's decoder configuration, when it supplied one.
   *
   * Opaque on purpose. A muxer needs it — an AAC track without its
   * `AudioSpecificConfig` is unplayable — but its shape is WebCodecs', and the
   * core's job is to carry it from the encoder that produced it to the muxer
   * that wants it, not to understand it. Present on the first chunk only, which
   * is how WebCodecs delivers it.
   */
  readonly decoderConfig?: unknown;
}

export type AudioEncoderState = 'unconfigured' | 'configured' | 'closed' | 'errored';

export interface AudioEncodeConfig {
  readonly codec: string;
  readonly sampleRate: number;
  readonly numberOfChannels: number;
  readonly bitrate: number;
}

/** The audio encoder, as the core sees it. */
export interface AudioEncoderAdapter {
  readonly state: AudioEncoderState;
  /** Chunks accepted but not yet encoded. The backpressure signal. */
  readonly queueSize: number;
  /** Chunks emitted so far. See `VideoEncoderAdapter.producedChunks`. */
  readonly producedChunks?: number;
  configure(config: AudioEncodeConfig): Promise<void>;
  /** Takes a chunk. Does **not** close it; the caller still owns it. */
  encode(chunk: AudioChunk): void;
  flush(): Promise<void>;
  close(): void;
}

// ---------------------------------------------------------------------------
// Codec selection
// ---------------------------------------------------------------------------

export type ContainerFormat = 'mp4' | 'webm';

export interface AudioCodecCandidate {
  readonly name: string;
  /** The codec string an encoder is configured with. */
  readonly codec: string;
  /** What a muxer calls this codec. */
  readonly muxerCodec: 'aac' | 'opus';
  /** Containers that can carry it. */
  readonly containers: readonly ContainerFormat[];
}

/**
 * The audio codecs worth trying, best first.
 *
 * AAC first because the target container is MP4 whenever the video allows it,
 * and an MP4 with AAC is the combination that opens in everything from a phone
 * gallery to an editing timeline. Opus is better per bit and is what WebM
 * wants, but an MP4 carrying Opus — legal since 2018 — is still refused by
 * enough players that it is not a good default.
 *
 * `mp4a.40.2` is AAC-LC, the profile every decoder implements. `mp4a.40.5`
 * (HE-AAC) would halve the bitrate and is not universally decodable, which for
 * a music export is the wrong trade.
 */
export const AUDIO_CODECS: readonly AudioCodecCandidate[] = [
  { name: 'AAC-LC', codec: 'mp4a.40.2', muxerCodec: 'aac', containers: ['mp4'] },
  { name: 'Opus', codec: 'opus', muxerCodec: 'opus', containers: ['webm', 'mp4'] },
];

/** Answers whether the host can encode with a given audio configuration. */
export type AudioCodecProbe = (config: AudioEncodeConfig) => Promise<boolean>;

export interface AudioCodecSelection {
  readonly candidate: AudioCodecCandidate;
  readonly config: AudioEncodeConfig;
  readonly rejected: readonly { readonly name: string; readonly reason: string }[];
}

/**
 * Picks an audio codec the host will actually accept.
 *
 * Same argument as `selectVideoCodec`: asking costs one `isConfigSupported` per
 * candidate and removes a whole class of "works on my machine". A probe that
 * throws counts as "no", because a browser that dislikes a codec string is as
 * likely to throw as to answer.
 *
 * ## Probe exactly what will be configured
 *
 * The bitrate is part of the question, not a detail to be filled in afterwards.
 * Support is per-configuration: this browser accepts AAC at 44 100 Hz mono at
 * 128 kbps and refuses the same codec, rate and channel count at 64. Probing
 * with one number and configuring with another produced exactly that — a codec
 * reported as available and then rejected on the next line, with a message
 * blaming the sample rate.
 *
 * So `bitrateFor` resolves the bitrate per candidate, and the configuration
 * that comes back is the one that was asked about.
 */
export async function selectAudioCodec(options: {
  readonly probe: AudioCodecProbe;
  readonly sampleRate: number;
  readonly channelCount: number;
  /** Used when `bitrateFor` is absent. */
  readonly bitrate: number;
  /** The bitrate this candidate would actually be configured with. */
  readonly bitrateFor?: (candidate: AudioCodecCandidate) => number;
  readonly candidates?: readonly AudioCodecCandidate[];
  /** Restricts the search to codecs a given container can carry. */
  readonly container?: ContainerFormat;
}): Promise<AudioCodecSelection | null> {
  const candidates = (options.candidates ?? AUDIO_CODECS).filter(
    (entry) => options.container === undefined || entry.containers.includes(options.container),
  );
  const rejected: { name: string; reason: string }[] = [];

  for (const candidate of candidates) {
    const config: AudioEncodeConfig = {
      codec: candidate.codec,
      sampleRate: options.sampleRate,
      numberOfChannels: options.channelCount,
      bitrate: options.bitrateFor?.(candidate) ?? options.bitrate,
    };
    let supported = false;
    try {
      supported = await options.probe(config);
    } catch (cause) {
      rejected.push({
        name: candidate.name,
        reason: cause instanceof Error ? cause.message : String(cause),
      });
      continue;
    }
    if (supported) return { candidate, config, rejected };
    rejected.push({ name: candidate.name, reason: 'not supported' });
  }
  return null;
}

/**
 * A sensible audio bitrate.
 *
 * 128 kbps for stereo AAC-LC is the point where most listeners stop hearing the
 * encoder on music; Opus reaches the same place around 96. Scaled by channel
 * count rather than fixed, so a mono song is not given twice the bits it needs.
 */
export function suggestedAudioBitrate(codec: AudioCodecCandidate, channelCount: number): number {
  const perChannel = codec.muxerCodec === 'opus' ? 48_000 : 64_000;
  return Math.round(perChannel * Math.max(1, Math.min(2, channelCount)));
}

// ---------------------------------------------------------------------------
// Timestamps
// ---------------------------------------------------------------------------

/**
 * The timestamp of a sample position, in microseconds.
 *
 * From the sample index and the sample rate, and from nothing else — the same
 * rule `frameTimestampMicros` follows for video and `OfflineRenderClock` follows
 * for time in seconds. Both tracks therefore describe the same timeline from the
 * same origin, which is what makes them line up in the container.
 *
 * The multiplication precedes the division so the arithmetic stays on integers
 * for as long as it can. Accumulating a per-chunk increment instead would drift —
 * at 44 100 Hz a 1024-frame chunk is 23 219.95 µs, and rounding that every chunk
 * gains 117 µs a minute, about half a millisecond over a four-minute song. That
 * is small; well under a frame, and not on its own a reason to care.
 *
 * The reason to care is that it is *history-dependent*. An accumulated timestamp
 * is a function of every chunk before it, so it changes if the chunk size
 * changes, if a chunk is dropped, or if the export is ever resumed — and two
 * exports of one performance would then disagree. Derived from the sample index
 * it is none of those things: exact, independent of how the song was divided,
 * and identical on every run. Determinism is the property being protected here,
 * and the drift is merely the visible symptom of not having it.
 */
export function audioTimestampMicros(sampleIndex: number, sampleRate: number): number {
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) return 0;
  return Math.round((sampleIndex * 1_000_000) / sampleRate);
}

/** How many sample frames a duration covers, rounded up so nothing is cut. */
export function audioFrameCount(durationSeconds: number, sampleRate: number): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0;
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) return 0;
  return Math.ceil(durationSeconds * sampleRate);
}

export interface AudioChunkPlan {
  readonly index: number;
  readonly startFrame: number;
  readonly frameCount: number;
  readonly timestampMicros: number;
  readonly durationMicros: number;
}

/**
 * How the song is divided for the encoder.
 *
 * Fixed-size chunks, except the last one, which is **whatever is left**. That
 * exception is the point of this function. Rounding the total down to a whole
 * number of chunks is the easy thing to write and it silently truncates the end
 * of every song that is not an exact multiple of the chunk size — at 1024 frames
 * and 44.1 kHz that is up to 23 ms of missing music, which is precisely the
 * final decay of the last note.
 *
 * The size itself is a compromise the encoder cares about: too small and the
 * per-call overhead dominates, too large and a single chunk is a long time for
 * the encoder to be busy while the video loop waits. 1024 frames is one AAC
 * frame, which is the granularity the encoder works in anyway.
 */
export function planAudioChunks(totalFrames: number, chunkFrames: number, sampleRate: number): AudioChunkPlan[] {
  const plans: AudioChunkPlan[] = [];
  if (totalFrames <= 0 || chunkFrames <= 0) return plans;

  let start = 0;
  let index = 0;
  while (start < totalFrames) {
    const count = Math.min(chunkFrames, totalFrames - start);
    const startMicros = audioTimestampMicros(start, sampleRate);
    plans.push({
      index,
      startFrame: start,
      frameCount: count,
      timestampMicros: startMicros,
      // From the two endpoints rather than from the count, so consecutive
      // durations tile the timeline exactly and the last one absorbs the
      // rounding instead of leaving a gap.
      durationMicros: audioTimestampMicros(start + count, sampleRate) - startMicros,
    });
    start += count;
    index += 1;
  }
  return plans;
}

/** The chunk size to ask for, given what the codec works in. */
export function defaultChunkFrames(codec: AudioCodecCandidate): number {
  // AAC encodes 1024 samples at a time and Opus works in 20 ms frames; feeding
  // either a multiple of its own granule avoids the encoder repacking every
  // chunk it is given.
  return codec.muxerCodec === 'opus' ? 960 * 2 : 1024;
}
